/**
 * Paginated league leaderboard: loads full list-holding members into a versioned cache,
 * then slices pages in memory. Used by LeaguePage LeaderboardCard.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FirebaseContext } from '@/session/FirebaseContext';
import type { Dict } from '@/helpers';
import {
  ensureLeaderboardCache,
  LEADERBOARD_MEMBERS_PER_PAGE,
  sliceLeaderboardPage,
  type LeaderboardCacheEntry,
} from '@/helpers/leagues/leagueLeaderboardCache';
import { getCompetingCount, getLastLeaderboardPageIndex } from '@/helpers/leagues/leagueMemberSync';
import type League from '@/models/league';
import type { leagueMember } from '@/models/user';

export function useLeaderboardPages(league: League & { id: string }) {
  const ctx = useContext(FirebaseContext);
  const membersPerPage = LEADERBOARD_MEMBERS_PER_PAGE;
  const competingCount = getCompetingCount(league);
  const ranksLastUpdatedMs = league.ranksLastUpdated?.toMillis?.() ?? 0;

  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cacheEntry, setCacheEntry] = useState<LeaderboardCacheEntry>();
  const [membersLoaded, setMembersLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPageIndex(0);
    setLoading(true);
    setMembersLoaded(false);
    setCacheEntry(undefined);

    void ensureLeaderboardCache(ctx.db, league)
      .then((entry) => {
        if (cancelled) return;
        setCacheEntry(entry);
        setMembersLoaded(true);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ctx.db, league.id, competingCount, ranksLastUpdatedMs]);

  const pageSlice = useMemo(() => {
    if (!cacheEntry) {
      return { memberIds: [] as string[], members: {} as Dict<leagueMember> };
    }
    return sliceLeaderboardPage(cacheEntry, pageIndex, membersPerPage);
  }, [cacheEntry, pageIndex, membersPerPage]);

  const totalPages = Math.max(1, Math.ceil(competingCount / membersPerPage));
  const lastPageIndex = getLastLeaderboardPageIndex(competingCount, membersPerPage);

  const goToFirstPage = useCallback(() => {
    if (loading || pageIndex <= 0) return;
    setPageIndex(0);
  }, [loading, pageIndex]);

  const goToPrevPage = useCallback(() => {
    if (loading || pageIndex <= 0) return;
    setPageIndex((index) => index - 1);
  }, [loading, pageIndex]);

  const goToNextPage = useCallback(() => {
    if (loading || pageIndex >= lastPageIndex) return;
    setPageIndex((index) => index + 1);
  }, [lastPageIndex, loading, pageIndex]);

  const goToLastPage = useCallback(() => {
    if (loading || pageIndex >= lastPageIndex) return;
    setPageIndex(lastPageIndex);
  }, [lastPageIndex, loading, pageIndex]);

  const canGoFirst = !loading && pageIndex > 0;
  const canGoPrev = !loading && pageIndex > 0;
  const canGoNext = !loading && pageIndex < lastPageIndex;
  const canGoLast = !loading && pageIndex < lastPageIndex;

  return {
    pageIndex,
    loading,
    members: pageSlice.members,
    memberIds: pageSlice.memberIds,
    membersLoaded,
    membersPerPage,
    totalPages,
    canGoFirst,
    canGoPrev,
    canGoNext,
    canGoLast,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  };
}
